import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  updateProfile,
  sendEmailVerification
} from 'firebase/auth'
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore'
import { auth, db } from '../config/firebase'

const USERS_COLLECTION = 'users'

/**
 * Convert Firebase error codes to user-friendly messages
 */
const getAuthErrorMessage = (errorCode) => {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'This email address is already registered. Please sign in or use a different email.'
    case 'auth/invalid-email':
      return 'Please enter a valid email address.'
    case 'auth/weak-password':
      return 'Password is too weak. Please use at least 6 characters.'
    case 'auth/user-not-found':
      return 'No account found with this email address.'
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.'
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.'
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.'
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled. Please try again.'
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different sign-in method.'
    default:
      return 'An error occurred. Please try again.'
  }
}

/**
 * Create user profile in Firestore
 */
const createUserProfile = async (user, additionalData = {}) => {
  const userProfile = {
    email: user.email || '',
    displayName: user.displayName || '',
    authProvider: additionalData.authProvider || 'email',
    emailVerified: user.emailVerified,
    roles: { player: true },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastLoginAt: serverTimestamp()
  }

  if (user.photoURL) userProfile.photoURL = user.photoURL
  if (user.phoneNumber) userProfile.phoneNumber = user.phoneNumber
  if (additionalData.phoneNumber) userProfile.phoneNumber = additionalData.phoneNumber
  if (additionalData.firstName) userProfile.firstName = additionalData.firstName
  if (additionalData.lastName) userProfile.lastName = additionalData.lastName

  await setDoc(doc(db, USERS_COLLECTION, user.uid), userProfile)
}

/**
 * Update last login time
 */
const updateLastLogin = async (uid) => {
  try {
    await updateDoc(doc(db, USERS_COLLECTION, uid), {
      lastLoginAt: serverTimestamp()
    })
  } catch (error) {
    console.error('Error updating last login:', error)
  }
}

/**
 * Register with email and password
 */
export const registerWithEmail = async ({ email, password, displayName, firstName, lastName }) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password)
    const user = userCredential.user

    // Update display name in Firebase Auth
    await updateProfile(user, { displayName })
    
    // Send email verification
    await sendEmailVerification(user)
    
    // Note: User profile in Firestore is now created automatically by Cloud Function (onUserCreated)
    // The Cloud Function will parse displayName to extract firstName and lastName

    return { success: true, data: user }
  } catch (error) {
    console.error('Error registering user:', error)
    return {
      success: false,
      error: {
        code: error.code || 'unknown',
        message: getAuthErrorMessage(error.code) || error.message || 'Registration failed'
      }
    }
  }
}

/**
 * Sign in with email and password
 */
export const signInWithEmail = async ({ email, password }) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    // Note: lastLoginAt is managed by Cloud Function or can be updated separately if needed
    return { success: true, data: userCredential.user }
  } catch (error) {
    console.error('Error signing in:', error)
    return {
      success: false,
      error: {
        code: error.code || 'unknown',
        message: getAuthErrorMessage(error.code) || error.message || 'Sign in failed'
      }
    }
  }
}

/**
 * Sign in with Google (existing users only)
 */
export const signInWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider()
    provider.setCustomParameters({
      prompt: 'select_account'
    })
    
    const userCredential = await signInWithPopup(auth, provider)
    const user = userCredential.user

    // Check if user profile exists
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, user.uid))
    if (!userDoc.exists()) {
      // User doesn't exist, sign them out and return error
      await firebaseSignOut(auth)
      return {
        success: false,
        error: {
          code: 'account-not-found',
          message: 'No account found. Please sign up first.'
        }
      }
    }
    
    // Note: lastLoginAt is managed by Cloud Function or can be updated separately if needed
    return { success: true, data: user }
  } catch (error) {
    console.error('Error signing in with Google:', error)
    return {
      success: false,
      error: {
        code: error.code || 'unknown',
        message: getAuthErrorMessage(error.code) || error.message || 'Google sign in failed'
      }
    }
  }
}

/**
 * Sign up with Google (creates new account)
 */
export const signUpWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider()
    provider.setCustomParameters({
      prompt: 'select_account'
    })
    
    const userCredential = await signInWithPopup(auth, provider)
    const user = userCredential.user

    // Check if user profile already exists
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, user.uid))
    if (userDoc.exists()) {
      // User already exists
      // Note: lastLoginAt is managed by Cloud Function or can be updated separately if needed
      return { success: true, data: user }
    }
    
    // Note: User profile in Firestore is now created automatically by Cloud Function (onUserCreated)
    // No need to manually create profile here
    return { success: true, data: user }
  } catch (error) {
    console.error('Error signing up with Google:', error)
    return {
      success: false,
      error: {
        code: error.code || 'unknown',
        message: getAuthErrorMessage(error.code) || error.message || 'Google sign up failed'
      }
    }
  }
}

/**
 * Sign out
 */
export const signOut = async () => {
  try {
    await firebaseSignOut(auth)
    return { success: true }
  } catch (error) {
    console.error('Error signing out:', error)
    return {
      success: false,
      error: {
        code: error.code || 'unknown',
        message: error.message || 'Sign out failed'
      }
    }
  }
}

/**
 * Get user profile from Firestore with retry logic
 * Retries up to 5 times with 1 second delay to wait for Cloud Function to create profile
 */
export const getUserProfile = async (uid, retries = 5) => {
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, uid))
    if (userDoc.exists()) {
      return { id: uid, ...userDoc.data() }
    }
    
    // If profile doesn't exist and we have retries left, wait and try again
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, 1000))
      return getUserProfile(uid, retries - 1)
    }
    
    return null
  } catch (error) {
    console.error('Error getting user profile:', error)
    return null
  }
}

/**
 * Update user profile in Firestore
 */
export const updateUserProfile = async (uid, updates) => {
  try {
    await updateDoc(doc(db, USERS_COLLECTION, uid), {
      ...updates,
      updatedAt: serverTimestamp()
    })
    return { success: true }
  } catch (error) {
    console.error('Error updating user profile:', error)
    return {
      success: false,
      error: {
        code: error.code || 'unknown',
        message: error.message || 'Profile update failed'
      }
    }
  }
}

/**
 * Resend email verification
 */
export const resendVerificationEmail = async () => {
  try {
    const user = auth.currentUser
    if (!user) {
      return {
        success: false,
        error: {
          code: 'no-user',
          message: 'No user is currently signed in'
        }
      }
    }

    if (user.emailVerified) {
      return {
        success: false,
        error: {
          code: 'already-verified',
          message: 'Your email is already verified'
        }
      }
    }

    await sendEmailVerification(user)
    return { success: true }
  } catch (error) {
    console.error('Error resending verification email:', error)
    
    // Handle rate limiting error
    if (error.code === 'auth/too-many-requests') {
      return {
        success: false,
        error: {
          code: error.code,
          message: 'Too many requests. Please wait a few minutes before trying again.'
        }
      }
    }
    
    return {
      success: false,
      error: {
        code: error.code || 'unknown',
        message: error.message || 'Failed to send verification email'
      }
    }
  }
}
